import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Map, Clock, CreditCard, PenTool, Car, Check, Star, Download, Menu, X } from 'lucide-react';
import CarScene3D from './components/CarScene3D';
import { ZoneXLogo3D } from './components/ZoneXLogo3D';
import { useNavigate } from 'react-router-dom';
import { MiniParkingScene } from './components/MiniParkingScene';
import { EnhancedSparePartsSection } from './EnhancedSections';
import { EnhancedSolutionsSection } from './EnhancedSections';
import MechanicWorkshopScene from './components/MiniMechanicsShop';
import ParkingMap3D from './components/ParkingMap3D';



// Main App Component
const ZoneXLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [showFullScene, setShowFullScene] = useState(true);
  const videoRef = useRef(null);
  const navigateTo = useNavigate(); // Simply rename the hook variable to match your existing function name

  // Function to handle smooth scrolling
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  // Handle scroll for animations and navbar transparency
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Hide full 3D scene after scrolling past the hero section
      if (window.scrollY > window.innerHeight * 0.8) {
        setShowFullScene(false);
      } else {
        setShowFullScene(true);
      }

      // Activate scroll animations
      const elements = document.querySelectorAll('.fadeIn');
      elements.forEach(el => {
        const position = el.getBoundingClientRect();

        // If element is in viewport
        if (position.top < window.innerHeight * 0.8) {
          el.classList.add('active');
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    // Trigger once on load
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-white min-h-screen text-black overflow-hidden">
      {/* 3D Scene - Full screen in hero section, then minimized */}
      {/* <div className={`transition-opacity duration-1000 ${showFullScene ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <CarScene3D />
      </div> */}
      {/* Video Background - Full screen in hero section, then minimized */}


      <div className={`fixed inset-0 w-full h-full transition-opacity duration-1000 ${showFullScene ? 'opacity-100' : 'opacity-0 pointer-events-none'} z-0`}>
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="w-full h-full object-cover " // Increased brightness to 200% and added 10% contrast
          >
            <source
              src="/Opem Vid.mp4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 "></div>
        </div>
      </div>



      {/* Hero Section with enhanced red effects */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        {/* Red accent gradient overlay */}


        {/* Floating red particles effect */}
        {/* <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/4 w-3 h-3 rounded-full bg-zonex-red/30 animate-float" style={{ animationDelay: '0s' }}></div>
          <div className="absolute top-1/3 left-1/2 w-2 h-2 rounded-full bg-zonex-red/40 animate-float" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute top-2/3 left-1/3 w-4 h-4 rounded-full bg-zonex-red/20 animate-float" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 right-1/4 w-5 h-5 rounded-full bg-zonex-red/30 animate-float" style={{ animationDelay: '1.5s' }}></div>
          <div className="absolute bottom-1/4 right-1/3 w-3 h-3 rounded-full bg-zonex-red/40 animate-float" style={{ animationDelay: '2s' }}></div>
        </div> */}

        {/* Content */}
        <div className="container mx-auto px-4 z-10 py-16">
          <div className="max-w-2xl backdrop-blur-sm bg-black/30 p-8 rounded-xl border border-zonex-red/30 shadow-red-lg">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 animate-fadeIn">
              Urban Mobility <span className="text-gradient-redStart text-zonex-red drop-shadow-md">Redefined</span> for The Modern Driver
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 animate-fadeIn animation-delay-200">
           Book parking spots in advance, connect with certified mechanics on demand, and access quality spare parts—all from one integrated platform. ZoneX delivers a seamless vehicle management experience designed for today's urban lifestyle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn animation-delay-300">
              <button
                onClick={() => scrollToSection('features')}
                className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center justify-center shadow-red-md"
              >
                Get Started
              </button>
              {/* <button className="border border-zonex-red/50 text-white px-8 py-3 rounded-full hover:bg-zonex-red/10 transition-all duration-300 flex items-center justify-center">
                Watch Demo
              </button> */}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section with enhanced red accents */}
      <section id="features" className="py-20 bg-zonex-lightGray relative">
        {/* Enhanced Red accent lights */}
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/3 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Comprehensive Vehicle Management Solutions</h2>
            <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
            ZoneX brings together intelligent parking technology, certified mechanical expertise, and quality automotive parts in one seamless platform—revolutionizing how urban drivers manage their vehicles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Map className="w-12 h-12 text-zonex-red" />,
                title: "Smart Parking",
                description: "Find and reserve parking spots in real-time across thousands of locations.",
                link: "/parking"
              },
              {
                icon: <PenTool className="w-12 h-12 text-zonex-red" />,
                title: "On-Demand Mechanics",
                description: "Book certified mechanics directly to your location when you need them most.",
                link: "/mechanics"
              },
              {
                icon: <Clock className="w-12 h-12 text-zonex-red" />,
                title: "Inhouse Technology",
                description: "Spare Parts and Boom Barriers.",
                link: "https://zonexsparesweb.web.app/",
                isExternal: true
              },
              {
                icon: <CreditCard className="w-12 h-12 text-zonex-red" />,
                title: "Partnership",
                description: "Collaborate seamlessly with our platform to enhance services, share resources.",
                link: "/contact"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-zonex-white p-6 rounded-xl hover:translate-y-[-8px] transition-all duration-300 group fadeIn border border-zonex-gray-200 hover:border-zonex-red/50 shadow-sm hover:shadow-red-md">
                <div className="mb-4 p-3 bg-zonex-red/10 inline-block rounded-lg">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-zonex-gray-900">{feature.title}</h3>
                <p className="text-zonex-gray-700">{feature.description}</p>
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a href={feature.link} className="text-zonex-red flex items-center">
                    Learn more <ChevronRight className="w-4 h-4 ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* <div className="mt-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 fadeIn">
                <h3 className="text-2xl md:text-3xl font-bold mb-4">Find Perfect Parking in Seconds</h3>
                <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
                <p className="text-gray-400 mb-6">
                  Our intelligent system matches you with the ideal parking spot based on your location, vehicle type, and duration needs.
                </p>
                <ul className="space-y-3">
                  {["Real-time availability updates", "Price comparison", "Reservation management", "Favorite locations"].map((item, index) => (
                    <li key={index} className="flex items-center bg-gradient-to-r from-zonex-red/10 to-transparent p-2 rounded-lg">
                      <Check className="w-5 h-5 text-zonex-red mr-2" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigateTo('/parking')}
                  className="bg-zonex-red text-white px-6 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-red-md"
                >
                  Explore Parking Features <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
              <div className="order-1 md:order-2 fadeIn">
                <div className="rounded-xl shadow-red-lg mx-auto transform md:rotate-3 hover:rotate-0 transition-all duration-500 border-4 border-zonex-red/20 overflow-hidden">
                  <ParkingMap3D />
                  <div className="absolute inset-0 bg-gradient-to-t from-zonex-red/30 to-transparent opacity-70"></div>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </section>


      {/* Solutions Section */}
      <EnhancedSolutionsSection />
      {/* How It Works Section with improved red highlights */}
      <section id="how-it-works" className="py-20 bg-zonex-white relative overflow-hidden">
        {/* Enhanced accent shapes */}
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-red-to-black opacity-30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How ZoneX Works</h2>
            <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Getting started with ZoneX is easy. Just follow these simple steps:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                number: "01",
                title: "Download & Sign Up",
                description: "Download the ZoneX app and create your account in seconds.",
                icon: <Download className="w-8 h-8" />
              },
              {
                number: "02",
                title: "Find & Reserve",
                description: "Search for available parking spots or mechanics near you.",
                icon: <Map className="w-8 h-8" />
              },
              {
                number: "03",
                title: "Navigate & Park",
                description: "Follow in-app directions to your reserved parking spot.",
                icon: <Car className="w-8 h-8" />
              },
              {
                number: "04",
                title: "Check Out & Pay",
                description: "Complete your session and pay securely through the app.",
                icon: <CreditCard className="w-8 h-8" />
              }
            ].map((step, index) => (
              <div
                key={index}
                className="relative group fadeIn"
              >
                <div className="bg-zonex-white p-8 rounded-xl h-full flex flex-col transition-all duration-300 border border-zonex-gray-200 hover:border-zonex-red/50 shadow-sm hover:shadow-red-md">
                  <div className="flex justify-between items-start mb-6">
                    <div className="p-3 bg-zonex-red/10 rounded-lg">
                      {step.icon}
                    </div>
                    <span className="text-4xl font-bold text-zonex-red/20">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-zonex-gray-900">{step.title}</h3>
                  <p className="text-zonex-gray-700">{step.description}</p>

                  {/* Enhanced connector line with red gradient */}
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-1 bg-gradient-to-r from-zonex-red/80 to-zonex-red/10"></div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add ZoneX 3D Logo for visual interest */}
          <div className="mt-16 fadeIn">
            <ZoneXLogo3D containerHeight="200px" />
          </div>
        </div>
      </section >

      {/* Enhanced Parking Section with richer red accents */}
      < section
        ref={useRef(null)}
        className="py-20 bg-zonex-darkGray relative overflow-hidden"
        id="parking-section"
      >
        {/* Enhanced accent shapes */}
        < div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/20 rounded-full blur-3xl animate-pulse" ></div >
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-red-to-black opacity-40 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Advanced Parking Management System</h2>
            <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
             Find and reserve parking spots in real-time with our AI-powered platform. ZoneX's intelligent system provides guaranteed spots at thousands of locations, real-time availability updates, and contactless check-in—eliminating parking stress completely.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="transition-all duration-1000 opacity-100 translate-y-0">
              <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/40 shadow-red-lg relative">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <source
                    src="/newzonevideo.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            <div className="transition-all duration-1000 delay-300 opacity-100 translate-x-0">
              <h3 className="text-2xl font-bold mb-4">Find Perfect Parking in Seconds</h3>
              <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
              <p className="text-gray-400 mb-8">
                Our intelligent system matches you with the ideal parking spot based on your location, vehicle type, and duration needs.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Real-time availability updates",
                  "Price comparison across locations",
                  "Reservation management system",
                  "Favorite locations for quick access",
                  "Contactless check-in and payment"
                ].map((item, index) => (
                  <li key={index} className="flex items-center bg-gradient-to-r from-zonex-red/20 to-transparent p-3 rounded-lg">
                    <div className="p-1 bg-zonex-red/20 rounded-full mr-3">
                      <Check className="w-4 h-4 text-zonex-red flex-shrink-0" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigateTo('/parking')}
                className="bg-zonex-red text-white px-6 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-red-md"
              >
                Explore Parking Features <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      </section >

      {/* Enhanced Mechanic Section with bolder red elements */}
      <section ref={useRef(null)} className="py-20 bg-zonex-white relative overflow-hidden" id="mechanic-section">
        {/* Red accent elements */}
        < div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/15 rounded-full blur-3xl" ></div >
        <div className="absolute top-1/3 right-1/4 w-1/5 h-1/5 bg-zonex-red/10 rounded-full blur-3xl"></div>

        {/* Animated car silhouettes in red */}
        <div className="absolute bottom-0 w-full h-32 opacity-30 overflow-hidden">
          <div className="car-silhouette car-1 bg-zonex-red shadow-red-glow"></div>
          <div className="car-silhouette car-2 bg-zonex-red shadow-red-glow"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Certified Mechanics at Your Fingertips</h2>
            <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
             Connect with qualified automotive technicians within minutes. Whether you need emergency repairs or scheduled maintenance, our verified mechanics come to your location, equipped with the right tools and expertise to get you back on the road.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 transition-all duration-1000 delay-300 opacity-100 translate-x-0">
              <h3 className="text-2xl font-bold mb-4 text-zonex-gray-900">Expert Service When You Need It</h3>
              <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
              <p className="text-zonex-gray-700 mb-8">
                From routine maintenance to emergency repairs, our network of certified mechanics has you covered.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-zonex-white p-5 rounded-xl border border-zonex-gray-200 shadow-sm">
                  <div className="p-3 bg-zonex-red/10 rounded-lg inline-flex mb-4">
                    <PenTool className="w-6 h-6 text-zonex-red" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-zonex-gray-900">Emergency Repairs</h4>
                  <p className="text-zonex-gray-700 text-sm">Quick response for breakdowns and urgent issues</p>
                </div>

                <div className="bg-zonex-white p-5 rounded-xl border border-zonex-gray-200 shadow-sm">
                  <div className="p-3 bg-zonex-red/10 rounded-lg inline-flex mb-4">
                    <Car className="w-6 h-6 text-zonex-red" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2 text-zonex-gray-900">Routine Maintenance</h4>
                  <p className="text-zonex-gray-700 text-sm">Schedule regular service at your convenience</p>
                </div>
              </div>

              <button
                onClick={() => navigateTo('/mechanics')}
                className="bg-zonex-red text-white px-6 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-red-md"
              >
                Book a Mechanic <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            <div className="order-1 lg:order-2 transition-all duration-1000 opacity-100 translate-y-0">
              {/* <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/40 shadow-red-lg animate-glow-border">
          
                <MechanicWorkshopScene containerHeight="400px" showMechanic={true} />
              </div> */}
              <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/40 shadow-red-lg animate-glow-border h-[500px] w-full">
                {/* <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/40 shadow-red-lg animate-glow-border h-[500px] w-[500px]"> */}
                <img
                  src="/Zonex_Car_Bike_Service.gif"
                  alt="Mechanic Workshop"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section >

      {/* New Spare Parts Section */}
      < EnhancedSparePartsSection />

      {/* Stats Section with red gradient background */}
      < section className="py-16 bg-gradient-to-r from-zonex-red to-zonex-lightRed relative overflow-hidden" >
        {/* Adding overlay elements for depth */}
        < div className="absolute inset-0 opacity-20" >
          <div className="absolute top-0 left-0 w-full h-2 bg-white/30"></div>
          <div className="absolute bottom-0 left-0 w-full h-2 bg-black/30"></div>
        </div >

        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "1500+", label: "Parking Locations" },
              { value: "50K+", label: "Daily Active Users" },
              { value: "200+", label: "Service Providers" },
              { value: "98%", label: "Customer Satisfaction" }
            ].map((stat, index) => (
              <div key={index} className="py-4 bg-white/10 backdrop-blur-sm rounded-xl shadow-xl">
                <div className="text-3xl md:text-4xl font-bold mb-2 drop-shadow-md">{stat.value}</div>
                <div className="text-sm uppercase tracking-wider text-white/90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section >

      {/* Testimonials Section with improved red accents */}
      < section id="testimonials" className="py-20 bg-zonex-darkGray relative" >
        {/* Red accent shapes */}
        < div className="absolute top-0 right-1/4 w-1/5 h-1/5 bg-zonex-red/15 rounded-full blur-3xl" ></div >
        <div className="absolute bottom-0 left-1/3 w-1/4 h-1/4 bg-zonex-red/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Users Say</h2>
            <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Thousands of drivers and businesses trust ZoneX for their parking and vehicle service needs.
            </p>
          </div>

          <div className="max-w-4xl mx-auto fadeIn">
            <div className="relative overflow-hidden p-4">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${0 * 100}%)` }}
              >
                {[
                  {
                    text: "ZoneX has transformed my daily commute. I save at least 15 minutes every morning by booking my spot in advance.",
                    author: "Sarah Johnson",
                    role: "Daily Commuter",
                    rating: 5
                  }
                ].map((testimonial, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 px-4"
                  >
                    <div className="bg-black p-8 rounded-xl shadow-xl border border-zonex-red/30">
                      <div className="flex mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`w-5 h-5 ${i < testimonial.rating ? 'text-zonex-red fill-zonex-red' : 'text-gray-600'}`}
                          />
                        ))}
                      </div>
                      <p className="text-xl text-gray-300 mb-6 italic">"{testimonial.text}"</p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-zonex-red/20 rounded-full mr-4 flex items-center justify-center">
                          <span className="text-zonex-red font-bold">{testimonial.author.charAt(0)}</span>
                        </div>
                        <div>
                          <h4 className="font-semibold">{testimonial.author}</h4>
                          <p className="text-sm text-zonex-red/80">{testimonial.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center mt-6 space-x-2">
              {[1, 2, 3].map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full ${index === 0 ? 'bg-zonex-red' : 'bg-gray-600'} transition-all duration-300`}
                />
              ))}
            </div>
          </div>
        </div>
      </section >

      {/* CTA Section with stronger red accents */}
      <section className="py-20 bg-zonex-lightGray relative overflow-hidden">
        {/* Enhanced 3D-inspired accent elements */}
        < div className="absolute top-0 left-0 w-1/2 h-1/2 bg-zonex-red/15 rounded-full blur-3xl" ></div >
        <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-zonex-red/25 rounded-full blur-3xl"></div>

        {/* Animated car silhouettes in the background */}
        <div className="absolute bottom-0 w-full h-32 opacity-30 overflow-hidden">
          <div className="car-silhouette car-1 bg-zonex-red/60 shadow-red-glow"></div>
          <div className="car-silhouette car-2 bg-zonex-red/60 shadow-red-glow"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="bg-zonex-white rounded-2xl p-8 md:p-12 shadow-lg border border-zonex-gray-300">
            <div className="md:flex items-center justify-between">
              <div className="mb-8 md:mb-0 md:max-w-lg">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-zonex-gray-900">Ready to Transform Your Parking Experience?</h2>
                <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
                <p className="text-zonex-gray-700">
                  Download the ZoneX app today and join thousands of users who have simplified their parking and vehicle maintenance.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigateTo('/contact')}
                  className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center justify-center shadow-red-md"
                >
                  Download for iOS
                </button>
                <button
                  onClick={() => navigateTo('/contact')}
                  className="border border-zonex-red/50 text-zonex-red px-8 py-3 rounded-full hover:bg-zonex-red/5 transition-all duration-300 flex items-center justify-center"
                >
                  Download for Android
                </button>
              </div>
            </div>
          </div>
        </div>
      </section >



      {/* Enhanced Custom CSS for animations and effects */}
      < style > {`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(252, 63, 63, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(252, 63, 63, 0); }
          100% { box-shadow: 0 0 0 0 rgba(252, 63, 63, 0); }
        }
        
        @keyframes glowBorder {
          0% { border-color: rgba(252, 63, 63, 0.2); }
          50% { border-color: rgba(252, 63, 63, 0.6); }
          100% { border-color: rgba(252, 63, 63, 0.2); }
        }
        
        @keyframes carMove1 {
          0% { transform: translateX(-100px); }
          100% { transform: translateX(calc(100vw + 100px)); }
        }
        
        @keyframes carMove2 {
          0% { transform: translateX(calc(100vw + 100px)); }
          100% { transform: translateX(-100px); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s forwards;
        }
        
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        
        .animation-delay-300 {
          animation-delay: 0.3s;
        }
        
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s infinite;
        }
        
        .animate-glow-border {
          animation: glowBorder 3s infinite;
        }
        
        /* Car silhouettes with enhanced red glow */
        .car-silhouette {
          position: absolute;
          width: 80px;
          height: 30px;
          background-color: rgba(252, 63, 63, 0.4);
          border-radius: 5px;
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.5);
        }
        
        .car-1 {
          bottom: 10px;
          animation: carMove1 15s linear infinite;
        }
        
        .car-2 {
          bottom: 50px;
          animation: carMove2 12s linear infinite;
        }
        
        /* Scroll animations */
        .fadeIn {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        
        .fadeIn.active {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Enhanced red button effects */
        button.bg-zonex-red {
          box-shadow: 0 10px 20px -10px rgba(252, 63, 63, 0.5);
          transition: all 0.3s ease;
        }
        
        button.bg-zonex-red:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 25px -10px rgba(252, 63, 63, 0.6);
        }
        
        /* Red gradient text effect */
        .text-gradient-redStart {
          background: linear-gradient(90deg, #FC3F3F, #FF6B6B);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          display: inline;
        }
        
        /* Shadow styles for red elements */
        .shadow-red-glow {
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
        }
      `}</style >
    </div >
  );
};

// Logo Component with enhanced red accent
// Logo Component with Image
const Logo = () => (
  <div className="flex items-center">
    <img
      src="/logo.png" // Replace with the actual path to your logo image
      alt="ZoneX"
      className="h-10 w-auto object-contain" // Adjust height as needed
    />
  </div>
);

// Nav Links Component with red hover effect
const NavLinks = ({ navigateTo }) => (
  <div className="flex space-x-6">
    <a href="#features" className="text-zonex-gray-800 hover:text-zonex-red transition-colors">Features</a>
    <a href="#how-it-works" className="text-zonex-gray-800 hover:text-zonex-red transition-colors">How It Works</a>
    <span
      onClick={() => navigateTo('/parking')}
      className="text-zonex-gray-800 hover:text-zonex-red transition-colors cursor-pointer"
    >
      Parking
    </span>
    <span
      onClick={() => navigateTo('/mechanics')}
      className="text-zonex-gray-800 hover:text-zonex-red transition-colors cursor-pointer"
    >
      Mechanics
    </span>
    <a href="#spare-parts-section" className="text-zonex-gray-800 hover:text-zonex-red transition-colors">Spares</a>
    <a href="#testimonials" className="text-zonex-gray-800 hover:text-zonex-red transition-colors">Testimonials</a>
    <span
      onClick={() => navigateTo('/about')}
      className="hover:text-zonex-gray-800 text-zonex-red transition-colors cursor-pointer"
    >
      About Us
    </span>
    {/* <span
      onClick={() => navigateTo('/contact')}
      className="hover:text-zonex-red transition-colors cursor-pointer"
    >
      Contact Us
    </span> */}

  </div>
);

// Mobile Nav Links Component with red hover effect
const MobileNavLinks = ({ setIsMenuOpen, navigateTo }) => (
  <div className="flex flex-col space-y-4">
    <a
      href="#features"
      className="hover:text-zonex-red transition-colors"
      onClick={() => setIsMenuOpen(false)}
    >
      Features
    </a>
    <a
      href="#how-it-works"
      className="hover:text-zonex-red transition-colors"
      onClick={() => setIsMenuOpen(false)}
    >
      How It Works
    </a>
    <span
      onClick={() => {
        navigateTo('/parking');
        setIsMenuOpen(false);
      }}
      className="hover:text-zonex-red transition-colors cursor-pointer"
    >
      Parking
    </span>
    <span
      onClick={() => {
        navigateTo('/mechanics');
        setIsMenuOpen(false);
      }}
      className="hover:text-zonex-red transition-colors cursor-pointer"
    >
      Mechanics
    </span>
    <span
      onClick={() => {
        navigateTo('/about');
        setIsMenuOpen(false);
      }}
      className="hover:text-zonex-red transition-colors cursor-pointer"
    >
      About Us
    </span>
    <a
      href="#testimonials"
      className="hover:text-zonex-red transition-colors"
      onClick={() => setIsMenuOpen(false)}
    >
      Testimonials
    </a>
  </div>
);

export default ZoneXLanding;